import * as THREE from 'three';
import React, { useRef, useEffect, useMemo } from 'react';
import { useFrame, useLoader } from '@react-three/fiber';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { TextureLoader } from 'three';

const SCALE = 12;
const LERP_FACTOR = 0.07;

type XYZ = {
    x: number;
    y: number;
    z: number;
}

interface PresetType {
    speed: XYZ;
    rotation: XYZ;
    position: XYZ;
}

interface Book3DModelProps {
    preset: number;
    transitionDuration?: number;
}

const _PRESET_01: PresetType = {
    speed: { x: 0, y: 0, z: 0 },
    rotation: { x: 90, y: 16, z: 0 },
    position: { x: 0, y: -20, z: 0 }
};

const _PRESET_02: PresetType = {
    speed: { x: 0, y: 0.2, z: 0 },
    rotation: { x: 90, y: 16, z: 0 },
    position: { x: 0, y: 25, z: 0 }
};

const _PRESET_03: PresetType = {
    speed: { x: 0, y: 0, z: 1 },
    rotation: { x: 90, y: 8, z: 0 },
    position: { x: 0, y: 0, z: 0 }
};

const _PRESET_04: PresetType = {
    speed: { x: 0, y: 0, z: 0 },
    rotation: { x: 90, y: 16, z: 0 },
    position: { x: 0, y: -20, z: 0 }
};

const _PRESET_05: PresetType = {
    speed: { x: 0, y: 0, z: 0 },
    rotation: { x: 45, y: 0, z: 0 },
    position: { x: 0, y: -30, z: 0 }
};

export default function Book3DModel({ preset, transitionDuration = 1 }: Book3DModelProps) {
    const obj = useLoader(OBJLoader, '/assets/modals/book_modal.obj');
    const texture = useLoader(TextureLoader, '/assets/modals/LECULTE_MAG_A24_COUVERTURE.png');
    const pivot = useRef<THREE.Group>(null);

    // Références pour le suivi des rotations et positions actuelles
    const currentRotation = useRef(new THREE.Vector3());
    const currentPosition = useRef(new THREE.Vector3());
    const targetPosition = useRef(new THREE.Vector3());

    // Sélection du preset actif
    const activePreset = useMemo(() => {
        switch(preset) {
            case 1: return _PRESET_01;
            case 2: return _PRESET_02;
            case 3: return _PRESET_03;
            case 4: return _PRESET_04;
            case 5: return _PRESET_05;
            default: return _PRESET_01;
        }
    }, [preset]);

    // Configuration initiale du modèle
    useEffect(() => {
        obj.traverse((child) => {
            if ((child as THREE.Mesh).isMesh) {
                const mesh = child as THREE.Mesh;
                mesh.scale.set(SCALE, SCALE, SCALE);

                const material = new THREE.MeshBasicMaterial({ map: texture });
                material.side = THREE.BackSide;
                mesh.material = material;
            }
        });

        const box = new THREE.Box3().setFromObject(obj);
        const center = box.getCenter(new THREE.Vector3());
        obj.position.sub(center);
        obj.position.y += box.getSize(new THREE.Vector3()).y / 2;

        // Initialisation des positions et rotations
        currentRotation.current.set(
            THREE.MathUtils.degToRad(activePreset.rotation.x),
            THREE.MathUtils.degToRad(activePreset.rotation.y),
            THREE.MathUtils.degToRad(activePreset.rotation.z)
        );
        currentPosition.current.set(
            activePreset.position.x,
            activePreset.position.y,
            activePreset.position.z
        );
        targetPosition.current.copy(currentPosition.current);
    }, [obj, texture]);

    // Mise à jour de la cible lorsque le preset change
    useEffect(() => {
        targetPosition.current.set(
            activePreset.position.x,
            activePreset.position.y,
            activePreset.position.z
        );
    }, [activePreset]);

    // Animation frame
    useFrame(({ clock }) => {
        if (pivot.current) {
            const elapsedTime = clock.getElapsedTime();

            // Lerp des rotations
            const targetRotationX = THREE.MathUtils.degToRad(activePreset.rotation.x);
            const targetRotationY = THREE.MathUtils.degToRad(activePreset.rotation.y);
            const targetRotationZ = THREE.MathUtils.degToRad(activePreset.rotation.z);

            currentRotation.current.x = THREE.MathUtils.lerp(
                currentRotation.current.x,
                targetRotationX,
                LERP_FACTOR
            );
            currentRotation.current.y = THREE.MathUtils.lerp(
                currentRotation.current.y,
                targetRotationY,
                LERP_FACTOR
            );
            currentRotation.current.z = THREE.MathUtils.lerp(
                currentRotation.current.z,
                targetRotationZ,
                LERP_FACTOR
            );

            // Application des rotations avec la vitesse continue
            pivot.current.rotation.x = currentRotation.current.x + elapsedTime * activePreset.speed.x;
            pivot.current.rotation.y = currentRotation.current.y + elapsedTime * activePreset.speed.y;
            pivot.current.rotation.z = currentRotation.current.z + elapsedTime * activePreset.speed.z;

            // Translation de la position
            currentPosition.current.lerp(targetPosition.current, LERP_FACTOR);
            pivot.current.position.copy(currentPosition.current);
        }
    });

    return (
        <group ref={pivot}>
            <primitive object={obj} />
        </group>
    );
}